import { useState } from "react";
import {
  Choices, Details, dimensionError, Disclaimer, NumberField, Nav, Panel, Progress, Question,
  ResultBlock, ResultFooter, ResultHeadline, ResultList, ToolFrame, useTool, useToolEvents, WorkableOptions,
} from "@/components/site/ToolShell";

const SLUG = "projector-room-fit";

// Throw ratio is a definition: throw distance divided by image width. It is never used here to
// claim compatibility with any particular projector — the homeowner is told the ratio their room
// requires so they can check it against a specific projector's published throw range.
// Units: wall and projector-to-screen distance are entered and displayed in FEET. Screen / image
// width is entered and displayed in INCHES everywhere. All ratio maths is normalised to inches.
function evaluate(f) {
  const wall = Number(f.wall) || 0;
  const depth = Number(f.depth) || 0;
  const desiredIn = Number(f.desired) || 0;
  const wallIn = Math.round(wall * 12);
  const inches = (n) => Math.round(n);

  const screenTooWide = desiredIn > 0 && desiredIn > wallIn;
  const usableWidthIn = desiredIn > 0 ? Math.min(desiredIn, wallIn) : wallIn;
  const ratio = usableWidthIn > 0 ? (depth * 12) / usableWidthIn : 0;
  const ratioLabel = ratio > 0 ? `${ratio.toFixed(2)}:1` : "—";

  const practical = wall >= 4 && depth >= 4 && !screenTooWide;

  let headline, body;
  if (screenTooWide) {
    headline = "Your desired screen is wider than your usable wall.";
    body = `You entered a ${inches(desiredIn)}-inch screen against ${wallIn} inches of usable wall. The screen cannot exceed the wall it sits on, so either re-measure the wall or reduce the target screen width.`;
  } else if (!practical) {
    headline = "Projection looks impractical in this room as measured.";
    body = "The usable wall or the available projector-to-screen distance is very small. Re-check the measurements, or consider a large direct-view display instead of projection.";
  } else {
    headline = "Projection appears practical in this room.";
    body = `With ${depth} ft of projector-to-screen distance and a ${inches(usableWidthIn)}-inch image width, your room requires a projector whose published throw ratio range includes approximately ${ratioLabel}.`;
  }

  const reasons = [];
  if (practical) {
    reasons.push(`Required throw ratio for a ${inches(usableWidthIn)}-inch-wide image: approximately ${ratioLabel}.`);
    reasons.push(`Usable screen wall: ${wallIn} inches — that is your maximum possible image width.`);
  }
  if (f.light === "Little") reasons.push("With little light control, ambient light will wash out the image unless you can darken the room or use an ambient-light-rejecting screen.");
  else if (f.light === "Some") reasons.push("With some light control, expect good results when darkened and reduced contrast at brighter times.");
  else reasons.push("Good light control is the single biggest advantage a projection room can have.");

  const placement = [];
  if (ratio > 0 && ratio < 1)
    placement.push(`A throw ratio near ${ratioLabel} is short-throw territory — confirm the projector you choose publishes a range that reaches it.`);
  else if (ratio >= 1 && ratio <= 2)
    placement.push(`A throw ratio near ${ratioLabel} sits in the range many standard home projectors publish, but you must still confirm it against the specific model.`);
  else if (ratio > 2)
    placement.push(`A throw ratio near ${ratioLabel} is long-throw territory, or it means you can use a smaller image and mount further forward.`);
  placement.push("Optical zoom and lens shift widen the placement options — check both on the exact model rather than assuming a single fixed distance.");
  if (f.mount === "Ceiling mount") placement.push("Confirm the ceiling mount depth and that the lens height works with the screen's vertical position.");
  if (f.mount === "Shelf or table behind seating") placement.push("Confirm the projector's throw at the shelf position and that heads and seat backs stay out of the light path.");
  if (f.mount === "Not decided yet") placement.push("Your mounting choice changes the usable throw distance, so settle it before ordering a projector.");

  const lightNotes = [];
  if (f.light === "Little") lightNotes.push("Consider an ambient-light-rejecting screen and prioritise projector brightness, or reconsider a direct-view display.");
  if (f.light === "Some") lightNotes.push("Blackout treatment on the main light sources usually buys more image quality than upgrading the projector.");
  if (f.light === "Good") lightNotes.push("Good control means you can prioritise contrast and colour accuracy over raw brightness.");

  // Directions derived from the ratio and measurements already calculated.
  const workable = [];
  if (screenTooWide) {
    workable.push(`Reduce the target screen width to your ${wallIn}-inch usable wall, or re-measure the wall`);
    workable.push("Re-run this check once the screen width and wall agree");
  } else if (!practical) {
    workable.push("Re-check the wall width and projector-to-screen distance — the measurements entered leave almost no working room");
    workable.push("A large direct-view display instead of projection in this room");
  } else {
    workable.push(`Projectors whose published throw-ratio range includes approximately ${ratioLabel}`);
    workable.push(`Screens up to approximately ${wallIn} inches wide, limited by your usable wall`);
    if (ratio > 0 && ratio < 1) workable.push("Short-throw projection, given the distance available");
    else if (ratio > 2) workable.push("Longer-throw projection, or a smaller image with the projector mounted further forward");
    else workable.push("Standard-throw projection, confirmed against the specific model's zoom range");
    if (f.light === "Little") workable.push("An ambient-light-rejecting screen, or a direct-view display if the room cannot be darkened");
    if (f.mount === "Ceiling mount") workable.push("A ceiling mount whose depth keeps the lens aligned with the screen's vertical position");
  }

  return {
    headline, body, reasons, practical, ratioLabel, usableWidthIn: inches(usableWidthIn),
    screenRange: practical ? `Up to approximately ${wallIn} inches wide, limited by your usable wall` : "Not established from these measurements",
    placement, lightNotes, workable,
    optionsHeadline: practical ? "What fits this room" : "What to do next in this room",
  };
}

const INITIAL = { light: "Some", mount: "Ceiling mount" };

export default function ProjectorFitChecker() {
  const tool = useTool(SLUG);
  const { start, complete, resetRun } = useToolEvents(SLUG);
  const [step, setStep] = useState(0);
  const [f, setF] = useState(INITIAL);
  const [showErrors, setShowErrors] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (v) => { start(); setF((s) => ({ ...s, [k]: v })); };
  const total = 2;

  const errs = {
    wall: dimensionError(f.wall, { min: 3, max: 40 }),
    depth: dimensionError(f.depth, { min: 3, max: 60 }),
    desired: f.desired === null || f.desired === undefined || f.desired === "" ? null : dimensionError(f.desired, { min: 30, max: 250, unit: "in" }),
  };
  const stepValid = step === 0 ? !errs.wall && !errs.depth && !errs.desired : true;

  const next = () => {
    start();
    if (!stepValid) return setShowErrors(true);
    setShowErrors(false);
    if (step < total - 1) return setStep(step + 1);
    const r = evaluate(f);
    setResult(r);
    complete({ practical: r.practical, ratio: r.ratioLabel });
  };

  if (!tool) return <div className="h-[60vh]" />;

  return (
    <ToolFrame tool={tool}>
      <Panel>
        {!result ? (
          <>
            <Progress step={step} total={total} />

            {step === 0 && (
              <Question
                label="Your room and screen"
                hint="Projector-to-screen distance is how far back the projector can sit. Leave the desired screen width blank and we will use the maximum your wall allows."
              >
                <div className="grid sm:grid-cols-3 gap-4">
                  <NumberField label="Usable screen-wall width" unit="ft" value={f.wall} onChange={set("wall")} testId="input-wall" placeholder="e.g. 11" error={errs.wall} showError={showErrors} />
                  <NumberField label="Projector-to-screen distance" unit="ft" value={f.depth} onChange={set("depth")} testId="input-depth" placeholder="e.g. 14" error={errs.depth} showError={showErrors} />
                  <NumberField label="Desired screen width (optional)" unit="in" value={f.desired} onChange={set("desired")} testId="input-desired" placeholder="e.g. 119" error={errs.desired} showError={showErrors} />
                </div>
              </Question>
            )}

            {step === 1 && (
              <>
                <Question label="How much light control does the room have?">
                  <Choices
                    testId="choice-light"
                    options={[
                      { value: "Good", label: "Good", note: "Can be made genuinely dark" },
                      { value: "Some", label: "Some", note: "Blinds or partial blackout" },
                      { value: "Little", label: "Little", note: "Bright room, hard to darken" },
                    ]}
                    value={f.light}
                    onChange={set("light")}
                  />
                </Question>
                <Question label="Where can the projector go?" hint="This determines your usable throw distance.">
                  <Choices
                    testId="choice-mount"
                    options={["Ceiling mount", "Shelf or table behind seating", "Not decided yet"]}
                    value={f.mount}
                    onChange={set("mount")}
                  />
                </Question>
              </>
            )}

            <Nav step={step} total={total} onBack={() => { setShowErrors(false); setStep(Math.max(0, step - 1)); }} onNext={next} disabled={!stepValid} />
          </>
        ) : (
          <div data-testid="tool-result">
            <ResultHeadline
              eyebrow="Your projector fit result"
              headline={result.headline}
              body={result.body}
              reasons={result.reasons.slice(0, 3)}
              testId="result-headline"
            />
            <WorkableOptions
              tool={tool}
              headline={result.optionsHeadline}
              options={result.workable}
              productPath={result.practical ? "Home projectors, screens and mounts matched to your measured room" : "Displays and screens appropriate to a room this size"}
            />
            <div className="border-t-2 border-charcoal mt-12">
              <ResultBlock label="Required throw ratio" value={result.ratioLabel} emphasis testId="result-ratio" />
              <ResultBlock label="Viable screen width" value={result.screenRange} testId="result-screen-range" />
              <ResultBlock label="Light control" value={`${f.light} — ${result.lightNotes[0] || ""}`} testId="result-light" />
              <Details label="See placement details" testId="result-details">
                <ResultList label="Placement and throw considerations" items={result.placement} testId="result-placement" />
              </Details>
            </div>

            <Disclaimer>
              Throw ratio is calculated from the measurements you entered. Room dimensions alone do
              not guarantee compatibility with any projector — you must check the required ratio
              against the specific projector's published throw-ratio and zoom range before ordering.
            </Disclaimer>

            <div className="mt-2">
              <ResultFooter onReset={() => { resetRun(); setResult(null); setStep(0); setF(INITIAL); setShowErrors(false); }} />
            </div>
          </div>
        )}
      </Panel>
    </ToolFrame>
  );
}
